// src/services/VehicleInfoService.js
import API_CONFIG from "./ApiConfig";

// In-memory cache (survives component re-mounts within same session)
const _cache = {
  makes: null,
  models: {},  // keyed by make name
  // Caribou NADA caches
  caribou_years: null,
  caribou_makes: {},  // keyed by year
  caribou_models: {}, // keyed by "year|make"
  caribou_trims: {},  // keyed by "year|make|model"
};

/**
 * Service for handling vehicle information API calls
 */
const VehicleInfoService = {
  /**
   * Fetch all available vehicle makes (cached after first call)
   */
  fetchVehicleMakes: async (token) => {
    // Return cached if available
    if (_cache.makes) return _cache.makes;

    // Check sessionStorage
    try {
      const cached = sessionStorage.getItem("edmunds_makes");
      if (cached) {
        const parsed = JSON.parse(cached);
        _cache.makes = parsed;
        return parsed;
      }
    } catch (e) { /* ignore parse errors */ }

    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/vehicle/info/edmunds/makes`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch vehicle makes");
      }

      const makes = await response.json();
      const makesArray = Array.isArray(makes) ? makes : (makes?.data || []);

      // Cache in memory and sessionStorage
      _cache.makes = makesArray;
      try { sessionStorage.setItem("edmunds_makes", JSON.stringify(makesArray)); } catch (e) { /* quota */ }

      return makesArray;
    } catch (error) {
      console.error("Error fetching vehicle makes:", error);
      throw error;
    }
  },

  /**
   * Fetch vehicle models for a specific make (cached per make)
   */
  fetchVehicleModels: async (makeName, token) => {
    const makeKey = makeName.toString().toLowerCase();

    // Return cached if available
    if (_cache.models[makeKey]) return _cache.models[makeKey];

    // Check sessionStorage
    try {
      const cached = sessionStorage.getItem(`edmunds_models_${makeKey}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        _cache.models[makeKey] = parsed;
        return parsed;
      }
    } catch (e) { /* ignore parse errors */ }

    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/vehicle/info/edmunds/models?make=${encodeURIComponent(makeKey)}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch vehicle models");
      }

      const models = await response.json();
      const modelsArray = Array.isArray(models) ? models : (models?.data || []);

      // Cache in memory and sessionStorage
      _cache.models[makeKey] = modelsArray;
      try { sessionStorage.setItem(`edmunds_models_${makeKey}`, JSON.stringify(modelsArray)); } catch (e) { /* quota */ }

      return modelsArray;
    } catch (error) {
      console.error("Error fetching vehicle models:", error);
      throw error;
    }
  },

  // ── Caribou NADA YMMT API (for refinancing flow) ─────────────────

  /**
   * Fetch vehicle years from Caribou NADA (cached)
   */
  fetchCaribouYears: async (token) => {
    if (_cache.caribou_years) return _cache.caribou_years;
    try {
      const cached = sessionStorage.getItem("caribou_years");
      if (cached) { _cache.caribou_years = JSON.parse(cached); return _cache.caribou_years; }
    } catch (e) {}

    const response = await fetch(
      `${API_CONFIG.BASE_URL}/api/vehicle/info/caribou/years`,
      { headers: { "Content-Type": "application/json", "x-user-token": token } }
    );
    if (!response.ok) throw new Error("Failed to fetch Caribou years");
    const data = await response.json();
    const years = Array.isArray(data) ? data : [];
    _cache.caribou_years = years;
    try { sessionStorage.setItem("caribou_years", JSON.stringify(years)); } catch (e) {}
    return years;
  },

  /**
   * Fetch vehicle makes for a year from Caribou NADA (cached per year)
   */
  fetchCaribouMakes: async (year, token) => {
    const key = year.toString();
    if (_cache.caribou_makes[key]) return _cache.caribou_makes[key];
    try {
      const cached = sessionStorage.getItem(`caribou_makes_${key}`);
      if (cached) { _cache.caribou_makes[key] = JSON.parse(cached); return _cache.caribou_makes[key]; }
    } catch (e) {}

    const response = await fetch(
      `${API_CONFIG.BASE_URL}/api/vehicle/info/caribou/makes?year=${encodeURIComponent(key)}`,
      { headers: { "Content-Type": "application/json", "x-user-token": token } }
    );
    if (!response.ok) throw new Error("Failed to fetch Caribou makes");
    const data = await response.json();
    const makes = Array.isArray(data) ? data : [];
    _cache.caribou_makes[key] = makes;
    try { sessionStorage.setItem(`caribou_makes_${key}`, JSON.stringify(makes)); } catch (e) {}
    return makes;
  },

  /**
   * Fetch vehicle models for a year+make from Caribou NADA (cached)
   */
  fetchCaribouModels: async (year, make, token) => {
    const key = `${year}|${make}`;
    if (_cache.caribou_models[key]) return _cache.caribou_models[key];
    try {
      const cached = sessionStorage.getItem(`caribou_models_${key}`);
      if (cached) { _cache.caribou_models[key] = JSON.parse(cached); return _cache.caribou_models[key]; }
    } catch (e) {}

    const response = await fetch(
      `${API_CONFIG.BASE_URL}/api/vehicle/info/caribou/models?year=${encodeURIComponent(year)}&make=${encodeURIComponent(make)}`,
      { headers: { "Content-Type": "application/json", "x-user-token": token } }
    );
    if (!response.ok) throw new Error("Failed to fetch Caribou models");
    const data = await response.json();
    const models = Array.isArray(data) ? data : [];
    _cache.caribou_models[key] = models;
    try { sessionStorage.setItem(`caribou_models_${key}`, JSON.stringify(models)); } catch (e) {}
    return models;
  },

  /**
   * Fetch vehicle trims for year+make+model from Caribou NADA (cached)
   * Returns [{tirm: "Sedan 4D ...", uid: "201939173"}, ...]
   */
  fetchCaribouTrims: async (year, make, model, token) => {
    const key = `${year}|${make}|${model}`;
    if (_cache.caribou_trims[key]) return _cache.caribou_trims[key];
    try {
      const cached = sessionStorage.getItem(`caribou_trims_${key}`);
      if (cached) { _cache.caribou_trims[key] = JSON.parse(cached); return _cache.caribou_trims[key]; }
    } catch (e) {}

    const response = await fetch(
      `${API_CONFIG.BASE_URL}/api/vehicle/info/caribou/trims?year=${encodeURIComponent(year)}&make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}`,
      { headers: { "Content-Type": "application/json", "x-user-token": token } }
    );
    if (!response.ok) throw new Error("Failed to fetch Caribou trims");
    const data = await response.json();
    const trims = Array.isArray(data) ? data : [];
    _cache.caribou_trims[key] = trims;
    try { sessionStorage.setItem(`caribou_trims_${key}`, JSON.stringify(trims)); } catch (e) {}
    return trims;
  },

  /**
   * Generate an array of years for vehicle selection
   */
  generateYearOptions: (yearsBack = 30) => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < yearsBack; i++) {
      years.push(currentYear - i);
    }
    return years;
  },

  /**
   * Validate VIN format
   */
  validateVIN: (vin) => {
    if (!vin || typeof vin !== 'string') return false;
    return vin.length === 17 && /^[A-HJ-NPR-Z0-9]{17}$/i.test(vin);
  },

  /**
   * Validate mileage input
   */
  validateMileage: (mileage) => {
    const mileageNum = parseInt(mileage);
    return !isNaN(mileageNum) && mileageNum >= 0 && mileageNum <= 999999;
  }
};

export default VehicleInfoService;
